/* eslint-disable camelcase */
/**
 * @author Fernando Ruano
 * @file saga.js
 * @description Menu Saga
 */

// Dependencies
import { put, call, takeLatest } from 'redux-saga/effects';

// Services
import menuService from '@nephro/services/menu-service';

// Global actions
import { setResponse, setLoading } from '@nephro/redux/stores/app/actions';

// Constants
import {
  GET_ALL_MENUS,
  GET_MENU_BY_ROLE,
  GET_MENU_BY_USER,
  GET_ALL_PERMISSIONS,
  GET_MAIN_MENU_BY_ROLE
} from './constants';

const customError = {
  type: 'Error',
  message: 'commons.problem'
};

/**
 * Call service to get menus from backend
 * @param { object } action - Action with payload data
 */
export function* getMenusArray(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const { callback } = action;

  try {
    const response = yield call(menuService.getMenus);
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to get menus by role from backend
 * @param { object } action - Action with payload data
 */
export function* getMenuByRole(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { id } = data;

  try {
    const response = yield call(menuService.getMenuByRole, id);
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to get main menus by role from backend
 * @param { object } action - Action with payload data
 */
export function* getMainMenuByRole(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { id } = data;

  try {
    const response = yield call(menuService.getMainMenuByRole, id);
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to get menus by user from backend
 * @param { object } action - Action with payload data
 */
export function* getMenuByUser(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { username } = data;

  try {
    const response = yield call(menuService.getMenuByUser, username);
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to get permissions from backend
 * @param { object } action - Action with payload data
 */
export function* getAllPermissions(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { paged, page, limit } = data;

  try {
    const response = yield call(
      menuService.getAllPermissions,
      paged,
      limit,
      page
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Declare all component sagas
 */
export function* watchMenuSaga() {
  yield takeLatest(GET_ALL_MENUS, getMenusArray);
  yield takeLatest(GET_MENU_BY_ROLE, getMenuByRole);
  yield takeLatest(GET_MAIN_MENU_BY_ROLE, getMainMenuByRole);
  yield takeLatest(GET_MENU_BY_USER, getMenuByUser);
  yield takeLatest(GET_ALL_PERMISSIONS, getAllPermissions);
}
